'use client';

import { useEffect } from 'react';

/**
 * Zeroes the horizontal residue on the LEFT-gutter float copies
 * (`.sidenote-left`, `.print-graph-slot-left`) of the bound-book layout.
 *
 * The book CSS (print.css "Bound-book layout") pulls the left copies out by
 * --gutter-pull, which lands them on the content-box edge only when their
 * containing block sits flush with the text column. A note anchored inside a
 * list item or blockquote inherits that container's left indent and falls
 * short by exactly that indent. The right copies are immune — every container
 * shares one RIGHT edge — so rather than enumerate every indented context
 * (list depths, blockquote padding, :::recs rails) in CSS, measure each float
 * once and fold the residue into its margin.
 *
 * Runs in the page (not the PDF render script) so plain CMD+P gets the same
 * treatment as `npm run pdf:*`. Margin edits are horizontal-only: the floats
 * keep a negative outer width, so line boxes — and therefore pagination — are
 * untouched. Idempotent: a second pass measures ~0 residue.
 */
export function PrintGutterAlign() {
  useEffect(() => {
    const essay = document.querySelector('.print-book .print-essay');
    if (!essay) return;
    const essayLeft = essay.getBoundingClientRect().left;
    const floats = essay.querySelectorAll<HTMLElement>(
      '.sidenote-left, .print-graph-slot-left',
    );
    for (const el of Array.from(floats)) {
      // Still trapped in a table cell: PrintTableSidenotes runs first (mount
      // order) and hoists in-table notes out, so anything left here is
      // untrappable — hide it rather than print it over the cell text. The
      // verso face loses that note; the recto face keeps the right copy.
      // The residue backstop catches traps beyond tables (a legitimate
      // indent — deepest known: a :::recs rail inside a nested list — stays
      // under ~71px).
      const residue = el.getBoundingClientRect().left - essayLeft;
      if (el.closest('td, th') || Math.abs(residue) > 100) {
        el.style.display = 'none';
        continue;
      }
      if (Math.abs(residue) < 0.5) continue;
      const current = parseFloat(getComputedStyle(el).marginLeft) || 0;
      el.style.marginLeft = `${current - residue}px`;
    }
  }, []);
  return null;
}
